import { difference, filter, isEmpty, map, uniq } from 'lodash'

/**
 * Extracts the root menu title from a menu title path.
 * The path format is expected to be "Root::Parent::Child::..."
 */
const getRootFromPath = path => {
  if (!path) return null
  const parts = path.split('::')
  return parts[0] || null
}

/**
 * Determines whether to split by grandParent, parent, or menuTitle level.
 * Returns the level and the unique root titles at that level.
 *
 * Edge cases:
 * - If there's only one unique grandParent, fall through to split by parent
 * - If there's only one unique parent (or one grandParent + one parent), split by menuTitle
 *
 * Important: We exclude container nodes that are parents of other nodes.
 * Only leaf-level nodes (those with actual dishes) should be in the roots.
 */
const determineRootLevel = menuContext => {
  // Get unique grandParent names
  const allGrandParentNames = uniq(
    map(
      filter(menuContext, ctx => ctx.grandParentMenuTitleName),
      'grandParentMenuTitleName',
    ),
  )

  // Get unique parent names
  const allParentNames = uniq(
    map(
      filter(menuContext, ctx => ctx.parentMenuTitleName),
      'parentMenuTitleName',
    ),
  )

  // Get unique menuTitle names
  const allMenuTitleNames = uniq(map(menuContext, 'menuTitleName'))

  // If we have multiple grandParents, split by grandParent
  // But exclude grandParents that don't have direct content (they're just containers)
  if (allGrandParentNames.length > 1) {
    return { level: 'grandParent', roots: allGrandParentNames }
  }

  // If we have multiple parents, split by parent
  // Exclude parents that are also grandParents (containers for other parents)
  if (allParentNames.length > 1) {
    const leafParents = difference(allParentNames, allGrandParentNames)
    if (leafParents.length > 1) {
      return { level: 'parent', roots: leafParents }
    }
  }

  // Split by menuTitle - exclude menu titles that are parents of other menu titles
  // Only include leaf nodes (menu titles that are not parents of anything else)
  const leafMenuTitles = difference(
    allMenuTitleNames,
    allParentNames,
    allGrandParentNames,
  )

  // If all menu titles are leaves, use them
  if (leafMenuTitles.length > 0) {
    return { level: 'menuTitle', roots: leafMenuTitles }
  }

  // Fallback to all menu titles if no clear leaves
  return { level: 'menuTitle', roots: allMenuTitleNames }
}

/**
 * Checks if a path contains a specific segment at any position.
 */
const pathContainsSegment = (path, segment) => {
  if (!path || !segment) return false
  const parts = path.split('::')
  return parts.includes(segment)
}

/**
 * Checks if a context item belongs to a given root based on the level.
 */
const contextBelongsToRoot = (context, root, level) => {
  switch (level) {
    case 'grandParent':
      // When splitting by grandParent, check if grandParent matches
      // or if the path starts with the root
      return (
        context.grandParentMenuTitleName === root ||
        getRootFromPath(context.menuTitlePath) === root
      )
    case 'parent':
      // When splitting by parent, check if parent matches
      // or if the second path segment matches (when grandParent exists)
      // or if the first path segment matches (when no grandParent)
      return (
        context.parentMenuTitleName === root ||
        pathContainsSegment(context.menuTitlePath, root)
      )
    case 'menuTitle':
      // When splitting by menuTitle, check if menuTitle matches
      return context.menuTitleName === root
    default:
      return false
  }
}

/**
 * Checks if a dish belongs to a given root based on its menuTitlePath.
 */
const dishBelongsToRoot = (dish, root, level) => {
  switch (level) {
    case 'grandParent':
      // First segment should be the root
      return getRootFromPath(dish.menuTitlePath) === root
    case 'parent':
      // The root (parent) should be somewhere in the path
      return pathContainsSegment(dish.menuTitlePath, root)
    case 'menuTitle':
      // The root (menuTitle) should be the last segment or in the path
      return pathContainsSegment(dish.menuTitlePath, root)
    default:
      return false
  }
}

/**
 * Splits menu data by root menu titles.
 *
 * @param {Object} menuData - The menu data containing dishes and menuContext
 * @param {Array} menuData.dishes - Array of dish objects
 * @param {Array} menuData.menuContext - Array of menu context objects
 * @returns {Array} Array of objects, each containing { rootTitle, dishes, menuContext }
 */
const splitMenuDataByMenuTitle = menuData => {
  const { dishes = [], menuContext = [] } = menuData

  if (isEmpty(menuContext)) {
    return [{ rootTitle: 'unknown', dishes, menuContext }]
  }

  const { level, roots } = determineRootLevel(menuContext)

  if (isEmpty(roots)) {
    return [{ rootTitle: 'unknown', dishes, menuContext }]
  }

  return map(roots, root => ({
    rootTitle: root,
    dishes: filter(dishes, dish => dishBelongsToRoot(dish, root, level)),
    menuContext: filter(menuContext, ctx =>
      contextBelongsToRoot(ctx, root, level),
    ),
  }))
}

export default splitMenuDataByMenuTitle
